import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { B2C } from 'cosmos-components';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { CaBrandNames } from 'src/enums';
import { SciPage } from 'src/components/shared/SciPage';
import { setCarouselDotsCurrentPage } from 'src/components/shared/SciPage/SciPage.duck';
import { sciBrandNameSelector } from 'src/components/shared/SciPage/SciPage.selector';
import { RedirectErrorModal } from 'src/components/shared/RedirectErrorModal';
import { SciPagesEnum } from 'src/components/shared/SciPage/types';
import { useSciContent } from 'src/hooks/useSciContent';
import { sciContentKeys } from 'src/constants';

import { colors } from 'src/themes';

import * as S from './payment.style';
import { MODULE_PREFIX } from './payment.constants';

import { usePayment } from './hooks/usePayment';
import msg from './payment.messages';
import { DueToday } from './dueToday.component';

import { PaymentForm } from './payment.form';

export const componentVehicleProtections = `${MODULE_PREFIX}-component`;

export const PaymentComponent: FC = () => {
    const {
        paymentAmount,
        paymentIssuers,
        paymentIssuerLoading,
        paymentSurcharge,
        paymentSurchargeIsLoading,
        needToConsiderSurcharge,
        isPaymentInProgress,
        selectedPaymentMethod,
        selectedPaymentIssuer,
        currency,
        control,
        error,
        paymentError,
        blockingError,
        dismissBlockingError,
        showTc,
        onSubmitManual,
        onSubmit,
    } = usePayment();

    const intl = useIntl();
    const brandName = useSelector(sciBrandNameSelector);
    const dispatch = useDispatch();
    const isCa = brandName === CaBrandNames.canadream;

    const title = isCa
        ? intl.formatMessage(msg.headerItems.titleCa)
        : intl.formatMessage(msg.headerItems.title);

    useEffect(() => {
        dispatch(setCarouselDotsCurrentPage(SciPagesEnum.Payment));
    }, [dispatch]);

    const {
        data: sciPaymentPageLabel,
        isLoading: isSciPaymentPageLabelLoading,
    } = useSciContent(sciContentKeys.sciPaymentPage);

    return (
        <B2C.LoaderContainer
            loading={isPaymentInProgress}
            size={'large'}
            opacity={{ opacity: '90%', color: colors.white }}
        >
            <SciPage data-test-id={componentVehicleProtections}>
                {blockingError && (
                    <RedirectErrorModal
                        title={intl.formatMessage(blockingError.title)}
                        description={intl.formatMessage(
                            blockingError.description,
                        )}
                        buttonLabel={intl.formatMessage(blockingError.button)}
                        redirectPath={blockingError.path}
                        onClose={dismissBlockingError}
                        testIdSuffix={blockingError.testIdKey}
                    />
                )}
                <S.Container>
                    <S.Header>
                        <S.PaymentTitle>{title}</S.PaymentTitle>
                        <S.PaymentText>
                            <B2C.LoaderContainer
                                loading={isSciPaymentPageLabelLoading}
                                size={'small'}
                            >
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: sciPaymentPageLabel ?? '',
                                    }}
                                ></span>
                            </B2C.LoaderContainer>
                        </S.PaymentText>
                    </S.Header>
                    <DueToday
                        paymentAmount={paymentAmount}
                        currency={currency}
                    />
                    <PaymentForm
                        paymentAmount={paymentAmount}
                        paymentIssuers={paymentIssuers ?? []}
                        paymentIssuerLoading={paymentIssuerLoading}
                        paymentSurcharge={paymentSurcharge}
                        paymentSurchargeIsLoading={paymentSurchargeIsLoading}
                        needToConsiderSurcharge={needToConsiderSurcharge}
                        selectedPaymentMethod={selectedPaymentMethod}
                        selectedPaymentIssuer={selectedPaymentIssuer}
                        isPaymentInProgress={isPaymentInProgress}
                        control={control}
                        error={error}
                        paymentError={paymentError}
                        showTc={showTc}
                        onSubmit={onSubmit}
                        onSubmitManual={onSubmitManual}
                    />
                </S.Container>
            </SciPage>
        </B2C.LoaderContainer>
    );
};
